import { SetOptional } from 'type-fest'

import { IQueryFilters, IQueryFiltersStripe, UserType } from '@utils/types'

/**
 * Regular expression for validating URLs with https protocol
 */
export const URL_REGEX =
  /((https:\/\/www\.|https:\/\/)+[a-zA-Z-]{2,}(\.[a-zA-Z-]{2,})(\.[a-zA-Z-]{2,})?)|(https:\/\/www\.|https:\/\/)+[a-zA-Z0-9-]{2,}\.[a-zA-Z0-9-]{2,}\.[a-zA-Z0-9-]{2,}(\.[a-zA-Z0-9-]{2,})?/g

/**
 * Gets the appropriate base path for a given user type
 * @param params - The parameters
 * @param params.userType - The user type to get path for
 * @returns The base path for the user type
 * @example
 * getPathForUserType({ userType: UserType.BUYER }) // returns '/buyer'
 * getPathForUserType({ userType: UserType.VENDOR }) // returns '/vendor'
 * getPathForUserType({ userType: UserType.ADMIN }) // returns '/admin'
 */
export const getPathForUserType = ({
  userType,
}: {
  userType: UserType
}): string => {
  switch (userType) {
    case UserType.ADMIN:
      return '/admin'
    case UserType.VENDOR:
      return '/vendor'
    case UserType.BUYER:
      return '/buyer'
    default:
      return '/'
  }
}

/**
 * Extracts the domain without subdomain from a given URL
 * @param href - The complete URL
 * @returns The domain without subdomain
 * @example
 * getDomainWithoutSubdomain('https://app.example.com/path') // returns 'example.com'
 * getDomainWithoutSubdomain('https://example.com/path') // returns 'example.com'
 */
export const getDomainWithoutSubdomain = (href: string) => {
  const url = new URL(href)
  const hostnameParts = url.hostname.split('.')

  if (hostnameParts.length > 2) {
    return hostnameParts.slice(-2).join('.')
  } else {
    return url.hostname
  }
}

/**
 * Gets the current domain URL (protocol + host)
 * @returns The current domain URL
 * @example
 * getDomainUrl() // returns 'https://localhost:3000' or 'https://app.example.com'
 */
export const getDomainUrl = () => {
  const protocol = window.location.protocol
  const host = window.location.host
  return `${protocol}//${host}`
}

/**
 * Builds a query string object from query filters for API requests
 * @param queryFilters - The query filters
 * @returns Query string object or undefined if no filters
 * @example
 * buildQueryString({
 *   filters: [{ field: 'status', value: 'active' }],
 *   page: 1,
 *   limit: 10
 * })
 * // returns { status: 'active', page: '1', limit: '10' }
 */
export const buildQueryString = (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
) => {
  if (!queryFilters) {
    return undefined
  }

  let query: { [key: string]: string } = {}

  const { filters, page, limit, negativeOffset, sortModel } = queryFilters

  if (!!filters) {
    const filtersQuery = filters?.reduce((acc, filter) => {
      const field = filter.field
      let value = filter.value

      if (value instanceof Date) {
        value = value.toISOString()
      }

      return {
        ...acc,
        [field]: value,
      }
    }, {})

    query = { ...query, ...filtersQuery }
  }

  if (!!page) {
    query = { ...query, page: String(page) }
  }

  if (!!limit) {
    query = { ...query, limit: String(limit) }
  }

  if (!!negativeOffset) {
    query = { ...query, negativeOffset: String(negativeOffset) }
  }

  if (sortModel?.sort) {
    query = { ...query, order: sortModel.field, sort: sortModel.sort }
  }

  return query
}

/**
 * Builds a query string object from query filters for Stripe API requests
 * @param queryFilters - The Stripe query filters
 * @returns Query string object or undefined if no filters
 * @example
 * buildQueryStringStripe({
 *   filters: [{ field: 'customer', value: 'cus_123' }],
 *   nextPageToken: 'token123',
 *   limit: 10
 * })
 * // returns { customer: 'cus_123', nextPageToken: 'token123', limit: '10' }
 */
export const buildQueryStringStripe = (
  queryFilters?: SetOptional<IQueryFiltersStripe, 'nextPageToken' | 'limit'>
) => {
  if (!queryFilters) {
    return undefined
  }

  let query: { [key: string]: string } = {}

  const { filters, nextPageToken, limit } = queryFilters

  if (!!filters) {
    const filtersQuery = filters?.reduce((acc, filter) => {
      const field = filter.field
      let value = filter.value

      if (value instanceof Date) {
        value = value.toISOString()
      }

      return {
        ...acc,
        [field]: value,
      }
    }, {})

    query = { ...query, ...filtersQuery }
  }

  if (!!nextPageToken) {
    query = { ...query, nextPageToken: String(nextPageToken) }
  }

  if (!!limit) {
    query = { ...query, limit: String(limit) }
  }

  return query
}

/**
 * Changes the browser's query string using direct history manipulation
 * This bypasses React Router and doesn't trigger location change events
 * @param params - The parameters
 * @param params.param - The query parameter name
 * @param params.value - The query parameter value (omit to clear)
 * @param params.push - Whether to push a new history entry or replace current
 * @example
 * changeQueryString({ param: 'tab', value: 'vendors', push: false })
 * // Changes URL from '/page' to '/page?tab=vendors'
 */
const changeQueryString = ({
  param,
  value,
  push,
}: {
  param: string
  value?: string
  push: boolean
}) => {
  const searchParams = new URLSearchParams(window.location.search)

  if (value) {
    searchParams.set(param, value)
  } else {
    searchParams.delete(param)
  }

  const newSearch = searchParams.toString()
  const newPath = `${window.location.pathname}${
    newSearch ? `?${newSearch}` : ''
  }${window.location.hash}`

  if (push) {
    window.history.pushState({}, '', newPath)
  } else {
    window.history.replaceState({}, '', newPath)
  }
}

/**
 * Changes the entire URL path using direct browser history manipulation
 * This bypasses React Router to avoid triggering location change listeners
 * @param params - The parameters
 * @param params.newPath - The new path to set
 * @param params.push - Whether to push a new history entry or replace current
 */
const changeUrlPath = ({
  newPath,
  push = false,
}: {
  newPath: string
  push?: boolean
}) => {
  if (push) {
    window.history.pushState({}, '', newPath)
  } else {
    window.history.replaceState({}, '', newPath)
  }
}

/**
 * Extracts and validates the user type from a URL hash.
 * Returns BUYER as default if hash is invalid or missing.
 * @param hash The URL hash (including or excluding #).
 * @returns The validated user type, defaults to BUYER if invalid.
 * @example
 * getUserTypeFromHash('#buyer') // returns UserType.BUYER
 * getUserTypeFromHash('#invalid') // returns UserType.BUYER
 */
const getUserTypeFromHash = (hash: string) => {
  if (!hash) {
    return UserType.BUYER
  }

  const userTypes = [UserType.BUYER, UserType.VENDOR] as string[]
  const userType = hash.replace('#', '')

  if (!userTypes.includes(userType)) {
    return UserType.BUYER
  }

  return userType as UserType
}

/**
 * Changes the URL hash without triggering a page scroll or history entry
 * @param newHash - The new hash value (without # symbol)
 */
const changeHash = (newHash: string) => {
  const newUrl = `${window.location.pathname}${window.location.search}#${newHash}`
  window.history.replaceState({}, '', newUrl)
}

/**
 * Removes the hash from the current URL
 */
const clearHash = () => {
  const newUrl = `${window.location.pathname}${window.location.search}`
  window.history.replaceState({}, '', newUrl)
}

/**
 * Opens a URL in a new browser tab with security measures
 * @param url - The URL to open
 */
export const openUrlInNewTab = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
  if (newWindow) {
    newWindow.opener = null
  }
}

/**
 * Resolves a dynamic user path by replacing '/user/' placeholder with the appropriate user type path
 * @param params - The parameters
 * @param params.path - The path containing '/user/' placeholder
 * @param params.userType - The user type to resolve
 * @returns The resolved path with user type
 * @example
 * resolveDynamicUserPath({ path: '/user/dashboard', userType: UserType.BUYER })
 * // returns '/buyer/dashboard'
 */
const resolveDynamicUserPath = ({
  path,
  userType,
}: {
  path: string
  userType: UserType
}): string => {
  const userBasePath = getPathForUserType({ userType })
  return path.replace('/user/', `${userBasePath}/`)
}

/**
 * Resolves URL with optional query parameter preservation
 * Handles 'preserveParams' query parameter to maintain certain URL parameters
 * @param params - The parameters
 * @param params.targetUrl - The target URL to navigate to
 * @param params.currentSearch - The current URL search parameters
 * @param params.paramsToRemove - Parameters to remove when preserving
 * @returns The resolved URL with or without preserved parameters
 * @example
 * resolvePreserveQueryParams({
 *   targetUrl: '/dashboard',
 *   currentSearch: '?tab=overview&preserveParams=true&other=value',
 *   paramsToRemove: ['preserveParams']
 * })
 * // returns '/dashboard?tab=overview&other=value'
 */
export const resolvePreserveQueryParams = ({
  targetUrl,
  currentSearch,
  paramsToRemove = ['redirect', 'preserveParams'],
}: {
  targetUrl: string
  currentSearch: string
  paramsToRemove?: string[]
}): string => {
  const searchParams = new URLSearchParams(currentSearch)
  const shouldPreserveParams = searchParams.get('preserveParams') === 'true'

  if (!shouldPreserveParams) {
    return targetUrl
  }

  // Remove specified parameters
  paramsToRemove.forEach((param) => searchParams.delete(param))

  const preservedParams = searchParams.toString()
  if (!preservedParams) {
    return targetUrl
  }

  const [basePath, existingParams] = targetUrl.split('?')
  const targetParams = new URLSearchParams(existingParams)

  // Merge preserved params with target params (target takes precedence)
  searchParams.forEach((value, key) => {
    if (!targetParams.has(key)) {
      targetParams.set(key, value)
    }
  })

  const finalParams = targetParams.toString()
  return `${basePath}${finalParams ? `?${finalParams}` : ''}`
}

export default {
  changeQueryString,
  changeUrlPath,
  getUserTypeFromHash,
  changeHash,
  clearHash,
  resolveDynamicUserPath,
  getDomainUrl,
  buildQueryString,
  resolvePreserveQueryParams,
}
